import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export type AlertType =
  | 'ws-disconnected'
  | 'ws-subscription-error'
  | 'token-expiring'
  | 'token-expired'
  | 'api-error';

export type AlertSeverity = 'warning' | 'error';

export interface Alert {
  id: string;
  type: AlertType;
  severity: AlertSeverity;
  message: string;
  timestamp: number;
  retryable: boolean;
}

export type WsStatus = 'connected' | 'connecting' | 'disconnected';

export const useConnectionStore = defineStore('connection', () => {
  const wsStatus = ref<WsStatus>('disconnected');
  const alerts = ref<Alert[]>([]);

  const hasAlerts = computed(() => alerts.value.length > 0);
  const hasCriticalAlert = computed(() =>
    alerts.value.some((a) => a.severity === 'error')
  );

  function setWsStatus(status: WsStatus) {
    wsStatus.value = status;

    if (status === 'connected') {
      clearByType('ws-disconnected');
    } else if (status === 'disconnected') {
      addAlert({
        type: 'ws-disconnected',
        severity: 'warning',
        message: 'Connection lost. Attempting to reconnect...',
        retryable: false,
      });
    }
  }

  function addAlert(partial: Omit<Alert, 'id' | 'timestamp'>) {
    // Don't duplicate alerts of the same type
    const existing = alerts.value.find((a) => a.type === partial.type);
    if (existing) return;

    alerts.value.push({
      ...partial,
      id: `${partial.type}-${Date.now()}`,
      timestamp: Date.now(),
    });
  }

  function clearAlert(id: string) {
    alerts.value = alerts.value.filter((a) => a.id !== id);
  }

  function clearByType(type: AlertType) {
    alerts.value = alerts.value.filter((a) => a.type !== type);
  }

  function clearAll() {
    alerts.value = [];
  }

  return {
    wsStatus,
    alerts,
    hasAlerts,
    hasCriticalAlert,
    setWsStatus,
    addAlert,
    clearAlert,
    clearByType,
    clearAll,
  };
});
